// ============================================
// TASK-9658 S1 — record deep links: `#/maintenance/vehicle/{id}`,
// `#/maintenance/task/{id}`, `#/pretrip/dvir/{id}`
// ============================================
// Side-effect module (UMD): attaches `window.RecordLinks`. app.js imports it
// ONCE, before any view module; app.js (router) and maintenance.js /
// pretrip.js (drains) read it through `window.RecordLinks` at CALL time —
// never a cross-`?v=` ES import (Pitfall #1). Node unit tests require() it:
// `node tests/task9658-record-links.unit.cjs`.
//
// HOW A LINK BECOMES AN OPEN RECORD (same shape as the HR deep link,
// `pendingHrFocus`, TASK-228):
//   1. app.js handleHashChange() parses the hash with parseRecordRoute(). Only
//      when the VIEW is in that session's allowedViews does it park
//      `AppState.pendingRecordFocus = { view, type, id, at }` — and then
//      navigates to the plain module view, where navigateToView() runs the
//      normal module-access gate (member modules / kiosk PIN / driver PIN).
//      A denied view never mounts, so the record is never drained.
//   2. The module's snapshot callback calls takePendingFocus(view) once its
//      data has landed, opens the record in its normal modal, or shows a plain
//      "not found" toast. Either way the focus is CONSUMED — it never lingers
//      to pop a modal on a later, unrelated snapshot.
//
// IDS ONLY. Links carry a Firestore document id — never a unit name, VIN, or
// person name (they ride in SMS bodies and email logs). The server twin that
// BUILDS these links is functions/src/utils/recordLinks.js; the drift guard in
// tests/task9658-record-links.unit.cjs pins the two route tables together.
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.RecordLinks = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // view → { url segment → record type }. Keep in lockstep with the server
  // twin's ROUTES (drift-guarded).
  const ROUTES = Object.freeze({
    maintenance: Object.freeze({ vehicle: 'vehicle', task: 'task' }),
    pretrip: Object.freeze({ dvir: 'dvir' }),
    // TASK-9587 — a job Claude asked for, opened in the Job Builder. The id is
    // an mcpProposals id; the mcpJobDraft callable decides who may open it.
    dispatch: Object.freeze({ 'claude-job': 'claudeJob', locate: 'locate' }), // TASK-9862 — a locate ticket
  });

  // A parked focus older than this is dropped instead of opened. Long enough to
  // cover a cold load that has to stop for a PIN / password sign-in; short
  // enough that a link tapped this morning can't pop a modal this afternoon.
  const FOCUS_TTL_MS = 10 * 60 * 1000;

  const MAX_ID_LEN = 200;

  // Firestore document-id rules (the subset that matters for a URL): no '/',
  // not '.' or '..', not the reserved __name__ form, non-empty, bounded.
  // Anything else is not a record id we could ever have emitted.
  function isPlausibleDocId(id) {
    if (typeof id !== 'string') return false;
    if (id.length === 0 || id.length > MAX_ID_LEN) return false;
    if (id.indexOf('/') !== -1) return false;
    if (id === '.' || id === '..') return false;
    if (/^__.*__$/.test(id)) return false;
    // Control characters / whitespace never appear in ids we generate.
    if (/[\u0000-\u001f\u007f\s]/.test(id)) return false;
    return true;
  }

  /**
   * Parse a location hash (with or without the leading `#/` / `#`) into a
   * record route. Returns `{ view, type, id }` or null. A `?query` suffix is
   * ignored, matching handleHashChange.
   */
  function parseRecordRoute(hash) {
    if (typeof hash !== 'string') return null;
    let h = hash;
    if (h.charAt(0) === '#') h = h.slice(1);
    if (h.charAt(0) === '/') h = h.slice(1);
    h = h.split('?')[0];
    const segs = h.split('/');
    // Exactly view/segment/id — a trailing slash or extra segment is not ours.
    if (segs.length !== 3) return null;
    const view = segs[0];
    const seg = segs[1];
    const table = Object.prototype.hasOwnProperty.call(ROUTES, view) ? ROUTES[view] : null;
    if (!table || !Object.prototype.hasOwnProperty.call(table, seg)) return null;
    let id;
    try {
      id = decodeURIComponent(segs[2]);
    } catch (_) {
      return null; // malformed %-encoding
    }
    if (!isPlausibleDocId(id)) return null;
    return { view, type: table[seg], id };
  }

  /** Build the hash path (no origin) for a record: `#/maintenance/task/abc`. */
  function buildRecordHash(view, type, id) {
    const table = Object.prototype.hasOwnProperty.call(ROUTES, view) ? ROUTES[view] : null;
    if (!table) return null;
    const seg = Object.keys(table).find((k) => table[k] === type);
    if (!seg || !isPlausibleDocId(id)) return null;
    return '#/' + view + '/' + seg + '/' + encodeURIComponent(id);
  }

  /**
   * Read-and-consume the parked focus for `view`. Returns `{ type, id }` or
   * null. Clears it when it belongs to this view (fresh or stale) so a module
   * opens a record at most once per link. A focus for a DIFFERENT view is left
   * alone. `appState` defaults to window.AppState.
   */
  function takePendingFocus(view, appState, now) {
    const as = appState || (typeof window !== 'undefined' ? window.AppState : null);
    if (!as) return null;
    const f = as.pendingRecordFocus;
    if (!f || f.view !== view) return null;
    as.pendingRecordFocus = null;
    const t = typeof now === 'number' ? now : Date.now();
    if (typeof f.at !== 'number' || t - f.at > FOCUS_TTL_MS || t < f.at - 60000) return null;
    if (!isPlausibleDocId(f.id)) return null;
    return { type: f.type, id: f.id };
  }

  /** Non-consuming check: is a fresh focus parked for `view`? */
  function hasPendingFocus(view, appState, now) {
    const as = appState || (typeof window !== 'undefined' ? window.AppState : null);
    const f = as && as.pendingRecordFocus;
    if (!f || (view && f.view !== view)) return false;
    const t = typeof now === 'number' ? now : Date.now();
    return typeof f.at === 'number' && t - f.at <= FOCUS_TTL_MS;
  }

  return {
    ROUTES,
    FOCUS_TTL_MS,
    isPlausibleDocId,
    parseRecordRoute,
    buildRecordHash,
    takePendingFocus,
    hasPendingFocus,
  };
});
